import { micActive } from 'icons/micActive';

import { ENDPOINT } from 'consts/api';
import { LIBRARY } from 'consts/consts';
import { VOICE_MODE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import { api } from 'helpers/api';
import {
	addClass,
	debug,
	disableInputArea,
	focusInputArea,
	isValidResponseData,
	readFromLs,
	removeClass,
} from 'helpers/helpers';
import { ENGT_CHAT } from 'js/bot.model';

import { EXT_ENDPOINT } from '../consts/api';
import { deleteFromLs, enableInputArea, initXMLhttp } from '../helpers/helpers';
import { disconnect, socketInit } from '../helpers/socket';

const MODE = {
	INPUT: 'input',
	LISTENING: 'listening',
	PROCESS_INPUT: 'processing_input',
	WAIT_FOR_RESPONSE: 'waiting_for_response',
	SPEAKING: 'speaking',
	EXISTING_VOICE: 'exiting_voice',
};

const getUserMedia = () =>
	navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;

const getSupportedMedia = () => getUserMedia() || (navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const addMediaRecorderScript = () => {
	const script = document.createElement('script');
	script.src = LIBRARY.RECORDER;
	document.head.appendChild(script);
};

const ENGT_SPEECH = {
	isPlaying: false,
	queue: [],
	player: new Audio(),
	stop() {
		this.player.pause();
		this.player.src = '';
		this.player.currentTime = 0;
		this.isPlaying = false;
		this.queue = [];
	},
};

ENGT_SPEECH.player.addEventListener('ended', () => {
	ENGT_SPEECH.isPlaying = false;

	if (ENGT_SPEECH.queue.length) {
		readOutMessage(ENGT_SPEECH.queue.pop());
	} else if (
		ENGT_CHAT.get('speech.allowContinuousMode') &&
		ENGT_CHAT.get('inputMode') !== VOICE_MODE.INPUT &&
		!ENGT_CHAT.get('speech.pauseAfterCurrentAudio')
	) {
		recorder.changeInputMode(VOICE_MODE.LISTENING);
	} else {
		recorder.changeInputMode(VOICE_MODE.INPUT);
	}
});

export const readOutMessage = (data) => {
	if (
		!data.message.voice_url &&
		ENGT_CHAT.get('speech.allowVoiceOutput') &&
		ENGT_CHAT.get('inputMode') === MODE.WAIT_FOR_RESPONSE
	) {
		clearTimeout(ENGT_CHAT.get('speech.nonVoiceTimeout'));

		ENGT_CHAT.set(
			'speech.nonVoiceTimeout',
			setTimeout(() => {
				recorder.changeInputMode('input');
				ENGT_CHAT.set('inputMode', 'input');
			}, 2500)
		);
	}
	data?.message?.voice_url && recorder.playAudio(data);
};

export let recorder = {
	engine: undefined,
	audioChunks: [],
	playAudio: (data) => {
		const { message } = data;
		const { voice_url } = message || {};

		recorder.changeInputMode(MODE.SPEAKING);

		if (voice_url && ENGT_CHAT.get('speech.allowVoiceOutput')) {
			clearTimeout(ENGT_CHAT.get('speech.nonVoiceTimeout'));

			if (
				ENGT_CHAT.get('inputMode') === VOICE_MODE.WAIT_FOR_RESPONSE ||
				ENGT_CHAT.get('inputMode') === VOICE_MODE.SPEAKING
			) {
				if (!ENGT_SPEECH.queue.length && !ENGT_SPEECH.isPlaying) {
					ENGT_SPEECH.player.src = voice_url;
					ENGT_SPEECH.player.play();
					ENGT_SPEECH.isPlaying = true;
				} else if (ENGT_SPEECH.isPlaying) {
					ENGT_SPEECH.queue.push(data);
				}

				ENGT_CHAT.set('speech.pauseAfterCurrentAudio', false);
			}
		}
	},
	stopAudio: () => {
		ENGT_SPEECH.stop();
	},
	changeInputMode: (mode) => {
		const inputMode = ENGT_CHAT.get('inputMode');
		if (inputMode === mode) {
			return;
		}

		debug(`state changed from ${inputMode} to ${mode}`);

		const engtNonInputMode = document.getElementById('engt-non-input-mode');
		const replyInputArea = document.querySelector('.engt-reply-area');
		const speech = { ...ENGT_CHAT.get('speech') };
		const engtVoiceWrapper = document.querySelector('#engtVoiceWrapper');
		const engtVoiceInputBtn = document.getElementById('engtVoiceInputBtn');
		const addClassStyle = (className, remove = true) => {
			remove && engtNonInputMode.removeAttribute('class');
			addClass(engtNonInputMode, className);
		};

		function changeInputMode() {
			recorder.stopAudio();
			addClassStyle('engt-hidden', false);
			replyInputArea.classList.remove('engt-hidden');
			engtVoiceWrapper.classList.remove('engtVoiceWrapper');
			engtVoiceInputBtn.innerHTML = micActive();
			!recorder.isMobileOrTablet && focusInputArea();
		}

		if (inputMode === MODE.SPEAKING && mode !== MODE.SPEAKING) {
			speech.audioObj.pause();
			speech.audioPlaying = false;
			speech.voiceQueue = [];
			ENGT_CHAT.set('speech', speech);
		}
		if (inputMode === MODE.LISTENING && mode !== MODE.LISTENING) {
			if (speech.allowSpeechRecognition) {
				speech.speechRecognizing = false;
				recorder.engine.stop();
			} else if (speech.allowAudioConversation) {
				speech.transactionId = null;
				recorder.stopRecording();
			}
		}

		ENGT_CHAT.set('inputMode', mode);

		switch (mode) {
			case MODE.INPUT:
				changeInputMode();
				enableInputArea();
				break;

			case MODE.LISTENING:
				addClassStyle('engt-voice-input');
				if (speech.allowSpeechRecognition) {
					recorder.startSpeechRecognition();
				} else if (speech.allowAudioConversation) {
					recorder.startRecording();
					if (recorder.isSafari) {
						addClassStyle('engt-init-mode', false);
						setTimeout(() => {
							removeClass(engtNonInputMode, 'engt-init-mode');
						}, 1500);
					}
				}
				break;
			case MODE.PROCESS_INPUT:
				if (ENGT_CHAT.get('config.isLLMEnabled') && ENGT_CHAT.get('config.isVoiceLLMConfigEnabled')) {
					recorder.handleLlmSpeechToTextProcessing();
				} else {
					addClassStyle('engt-processing-mode');
				}
				break;
			case MODE.WAIT_FOR_RESPONSE:
				addClassStyle('engt-waiting-mode');
				break;
			case MODE.SPEAKING:
				addClassStyle('engt-speaking-mode');
				break;
			case MODE.EXISTING_VOICE:
				addClassStyle('engt-exiting-input-mode');

				setTimeout(() => {
					recorder.changeInputMode(MODE.INPUT);
				}, 1500);
				break;
			default:
				addClassStyle('engt-hidden', false);
				!ENGT_CHAT.get('isMobileOrTablet') && focusInputArea();
		}
	},
	startRecording() {
		const speech = ENGT_CHAT.get('speech');
		const constraints = {
			audio: true,
		};

		const onSuccess = (stream) => {
			// eslint-disable-next-line no-undef
			speech.mediaRecorder = new MediaStreamRecorder(stream);
			speech.stream = stream;
			speech.mediaRecorder.mimeType = 'audio/wav';
			speech.mediaRecorder.audioChannels = 1;
			speech.mediaRecorder.ondataavailable = function (file) {
				if (speech.transactionId && file.size <= speech.MIN_AUDIO_RECORDING_FILE_SIZE) {
					const data = {
						file,
						fileUploadBaseURL: ENGT_CHAT.get('fileUploadBaseURL'),
						finalTransactionId: speech.transactionId,
						bot_ref: ENGT_CHAT.get('bot_ref'),
					};
					recorder.changeInputMode(MODE.PROCESS_INPUT);
					recorder.upload(data);
				} else {
					recorder.changeInputMode(MODE.INPUT);
				}
				recorder.stop();
				speech.transactionId = null;
			};
			speech.mediaRecorder.start(speech.MAX_RECORD_TIME);
		};

		const onError = (e) => {
			console.error('media error', e);
		};

		if (navigator.getUserMedia) {
			navigator.getUserMedia(constraints, onSuccess, onError);
		} else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
			navigator.mediaDevices.getUserMedia(constraints).then(onSuccess).catch(onError);
		}
		const message = {
			type: 'audio',
			text: null,
			fusToken: null,
			domain: ENGT_CHAT.get('config.brandedServer'),
		};
		speech.transactionId = null;
		sendMsg(message, false);
		// ENGT_CHAT.sendMessage(message); //refactor needed
	},
	stopRecording() {
		ENGT_CHAT.get('speech').mediaRecorder.stop();
		ENGT_CHAT.get('speech')
			.stream.getTracks()
			.forEach((track) => {
				track.stop();
			});
	},
	upload(output) {
		const { file } = output;
		const { finalTransactionId } = output;
		const botRef = output.bot_ref;
		const data = new FormData();
		data.append('file', file);
		data.append('workflow', 'VOICE_UPLOAD_WITH_EXPIRE');
		data.append('application', 'engati-voice-service');

		const onError = (error) => {
			recorder.changeInputMode(MODE.INPUT);
			debug(`Error occured in upload audio${error.message}`);
		};

		try {
			const uploadAudioAttachment = () =>
				api('GET', ENDPOINT().ATTACHMENTS, {
					async: false,
					headers: {
						transactionId: finalTransactionId,
						customInfo: JSON.stringify({
							user_id: ENGT_CHAT.get('status.sessionKey'),
							bot_ref: botRef,
							timeStamp: new Date().getTime(),
						}),
					},
				})
					.then((resp) => (isValidResponseData(resp) ? JSON.parse(resp.responseText) : {}))
					.then((resp) => {
						const message = {
							type: 'audio',
							text: null,
							fusToken: resp.responseObject.token,
							domain: ENGT_CHAT.get('config.brandedServer'),
						};
						// ENGT_CHAT.sendMessage(message); // Refactor needed
					})
					.catch(onError);

			uploadAudioAttachment();
		} catch (error) {
			onError(error);
		}
	},

	updateInputArea(value, content, isContentEditable) {
		const messageInput = document.getElementById('engtUserMessage');
		messageInput.setAttribute('contenteditable', isContentEditable);
		messageInput.innerHTML = content;
		const micIcon = document.getElementById('engtVoiceInputBtn');
		micIcon.disabled = value;
	},

	handleLlmSpeechToTextProcessing() {
		this.updateInputArea(true, ENGT_CHAT.get('config.processingInputText'), 'false');
		if (readFromLs(LS().STOP_AUDIO_RECORDING)) {
			recorder.changeInputMode(MODE.INPUT);
			recorder.updateInputArea(false, '', 'true');
			deleteFromLs(LS().STOP_AUDIO_RECORDING);
			return;
		}
		const audioBlob = new Blob(recorder.audioChunks, { type: 'audio/wav' });
		const data = {
			audioBlob,
			bot_ref: ENGT_CHAT.get('config.botRef'),
			bot_key: ENGT_CHAT.get('config.bot_key'),
			user_id: ENGT_CHAT.get('status.sessionKey'),
		};
		recorder.speechToTextForLLM(data);
	},
	speechToTextForLLM(output) {
		const http = initXMLhttp();
		const { audioBlob, bot_ref: botRef, bot_key: botKey, user_id: userId } = output;
		const data = new FormData();
		const temp_json = JSON.stringify({
			bot_key: botKey,
			user_id: userId,
			timestamp: new Date().getTime(),
		});
		data.append('file', audioBlob);
		data.append('request', temp_json);
		const onError = (error) => {
			this.updateInputArea(false, '', 'true');
			recorder.changeInputMode(MODE.INPUT);
			debug(`Error occured in LLM speech to text api call: ${error.message}`);
		};

		try {
			http.open('POST', EXT_ENDPOINT().VOICE_LLM_STT_PATH, true);
			http.onload = function () {
				let respJson;
				try {
					respJson = JSON.parse(http.response);
				} catch (e) {
					respJson = http.responseText;
				}

				if (this.readyState === 4 && this.status === 200) {
					if (respJson?.status?.code === 1000) {
						const text = respJson.response.speechToText;
						recorder.changeInputMode(MODE.INPUT);
						recorder.updateInputArea(false, text, 'true');
					} else {
						onError(respJson.status);
					}
				}
			};
			http.send(data);
		} catch (error) {
			onError(error);
		}
	},
	startSpeechRecognition: () => {
		const speech = ENGT_CHAT.get('speech');
		if (!speech.speechRecognizing) {
			if (!ENGT_CHAT.get('config.isLLMEnabled') || !ENGT_CHAT.get('config.isVoiceLLMConfigEnabled')) {
				recorder.engine.continuous = false;
				recorder.engine.interimResults = true;
				recorder.engine.lang = ENGT_CHAT.get('userLocale') || 'en-US';
			}
			recorder.engine.start();
			disableInputArea();
		} else {
			recorder.engine.stop();
		}
	},
};

export const setupSpeech = (resp) => {
	const { voice_config, voice_input_enabled } = resp || {};
	const isFirefox = navigator.userAgent.indexOf('Firefox') != -1;
	const EngtSpeechRecognition = isFirefox
		? false
		: window.SpeechRecognition || window.webkitSpeechRecognition || null;
	const supportedMedia = getSupportedMedia();

	const isAudioConvEnabled = voice_config && voice_config.audio_conversation_enabled;
	const allowSpeechRecognition =
		EngtSpeechRecognition && (isFirefox ? false : voice_input_enabled || isAudioConvEnabled);
	const allowAudioConversation = !allowSpeechRecognition && supportedMedia && isAudioConvEnabled;
	const allowContinuousMode =
		(allowSpeechRecognition || allowAudioConversation) && voice_config && voice_config.enable_continuous_voice;
	const allowVoiceOutput = voice_config && voice_config.audio_conversation_enabled && !!Audio;
	let speech = ENGT_CHAT.get('speech');
	speech = {
		...speech,
		allowSpeechRecognition,
		allowAudioConversation,
		allowContinuousMode,
		allowVoiceOutput,
	};

	if (allowAudioConversation) {
		navigator.getUserMedia = getUserMedia();
		addMediaRecorderScript();
	}

	if (allowVoiceOutput) {
		speech.audioObj = new Audio();
		speech.audioObj.addEventListener('ended', () => {
			debug('audio ended');
			speech.audioPlaying = false;
			if (speech.voiceQueue.length) {
				recorder.playAudio(speech.voiceQueue[0]);
				speech.voiceQueue.shift();
			} else if (
				allowContinuousMode &&
				ENGT_CHAT.get('inputMode') !== MODE.INPUT &&
				!speech.pauseAfterCurrentAudio
			) {
				recorder.changeInputMode(MODE.LISTENING);
			} else {
				recorder.changeInputMode(MODE.INPUT);
			}
		});
	}
	ENGT_CHAT.set('speech', speech);

	async function initializeMediaRecorder() {
		if (navigator.mediaDevices?.getUserMedia) {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				const engtMedia = new MediaRecorder(stream);
				speech.mediaRecorder = engtMedia;
				speech.stream = stream;
				ENGT_CHAT.set('speech', speech);
				return engtMedia;
			} catch (err) {
				console.error('The following error occurred: ' + err);
			}
		} else {
			console.error('getUserMedia is not supported in this browser');
		}
	}

	async function initializeSpeechEngine() {
		const engine =
			ENGT_CHAT.get('config.isLLMEnabled') && ENGT_CHAT.get('config.isVoiceLLMConfigEnabled')
				? await initializeMediaRecorder()
				: new EngtSpeechRecognition();

		return engine;
	}

	if (speech.allowSpeechRecognition) {
		let finalTranscript = '';
		initializeSpeechEngine()
			.then((engine) => {
				engine.onerror = function (event) {
					debug(event);
					speech.speechRecognizing = false;
					finalTranscript = '';
				};

				engine.onstart = function () {
					debug('Speech recognition service has started');
					speech.speechRecognizing = true;
					finalTranscript = '';
					recorder.audioChunks = [];
				};

				engine.ondataavailable = (event) => {
					recorder.audioChunks.push(event.data);
				};

				engine.onend = function () {
					debug('Speech recognition service disconnected');
					if (speech.speechRecognizing && finalTranscript) {
						if (speech.allowVoiceOutput) {
							const msgContainer = document.querySelector('.engt-sheet-content');
							const message = {
								type: 'audio',
								text: finalTranscript,
								fusToken: null,
								domain: ENGT_CHAT.get('config.brandedServer'),
							};
							sendMsg(message, false);
							recorder.changeInputMode(MODE.WAIT_FOR_RESPONSE);
						} else {
							sendMsg(finalTranscript, false);
							if (ENGT_CHAT.get('inputMode') === MODE.LISTENING) {
								recorder.changeInputMode(MODE.INPUT);
							}
						}
					} else if (ENGT_CHAT.get('inputMode') === MODE.LISTENING) {
						recorder.changeInputMode(MODE.EXISTING_VOICE);
					}
					speech.speechRecognizing = false;
					finalTranscript = '';
				};

				engine.onstop = function () {
					debug('Media Recorder stream stopped');
					recorder.changeInputMode(MODE.PROCESS_INPUT);
					speech.speechRecognizing = false;
				};

				engine.onresult = function (event) {
					for (let i = event.resultIndex; i < event.results.length; ++i) {
						if (event.results[i].isFinal) {
							finalTranscript += event.results[i][0].transcript;
						}
					}
				};

				recorder.engine = engine;
			})
			.catch((err) => {
				console.error('Error initializing speech engine: ', err);
			});
	}
};

export const sendMsg = (message, focusTextArea) => {
	if (focusTextArea) {
		document.getElementById('engtUserMessage').focus();
	}
	if (message !== '') {
		if (readFromLs(LS().GOT_UID) === 'true') {
			if (typeof message !== 'object') {
				ENGT_CHAT.get('sendMessage')(message);
			} else {
				ENGT_CHAT.get('sendMessage')(message.text, { message });
			}
		} else {
			disconnect();
			socketInit();
		}
	}
};
